import * as React from "react";
import {
  Callout,
  Classes,
  Dialog,
  Button,
  Divider,
  Intent,
  Tag,
} from "@blueprintjs/core";

import api, { HTTPError } from "../../services/api";
import { downloadErrorLog } from "../../utils/downloadErrorLog";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  modelInstanceId: number;
  /** Called after a successful upload with the insert/skip counts so the parent
   *  can close the dialog, show a Toaster, and refresh dimension data. */
  onUploadSuccess: (result: {
    inserted: number;
    skipped: number;
    upload_id: number;
    blob_name: string;
  }) => void;
}

interface IState {
  isDownloading: boolean;
  downloadError: string;
  selectedFile: File | null;
  isUploading: boolean;
  uploadError: string;
}

class DimensionInstanceBulkDialog extends React.Component<IProps, IState> {
  private fileInputRef = React.createRef<HTMLInputElement>();

  public state: IState = {
    isDownloading: false,
    downloadError: "",
    selectedFile: null,
    isUploading: false,
    uploadError: "",
  };

  public componentDidUpdate(prevProps: IProps) {
    // Reset state when the dialog is reopened so it always starts fresh.
    if (!prevProps.isOpen && this.props.isOpen) {
      this.setState({
        downloadError: "",
        selectedFile: null,
        isUploading: false,
        uploadError: "",
      });
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = "";
      }
    }
  }

  public render() {
    const {
      isDownloading,
      downloadError,
      selectedFile,
      isUploading,
      uploadError,
    } = this.state;

    return (
      <Dialog
        isOpen={this.props.isOpen}
        onClose={this.handleClose}
        title="Download/Upload Dimension Instances"
        style={{ width: 540 }}
        canOutsideClickClose={!isUploading}
        canEscapeKeyClose={!isUploading}
      >
        <div className={Classes.DIALOG_BODY}>
          {/* ---- Download section ---- */}
          <p className={Classes.RUNNING_TEXT}>
            Download an Excel template pre-populated with your existing
            dimension instances. The header row and pre-filled cells are{" "}
            <strong>locked</strong>. Add new dimension instance names in the
            blank cells below each dimension column.
          </p>
          {downloadError && (
            <Callout
              intent={Intent.DANGER}
              icon="error"
              style={{ marginBottom: 10 }}
            >
              {downloadError}
            </Callout>
          )}
          <Button
            intent={Intent.PRIMARY}
            icon="download"
            text="Download"
            loading={isDownloading}
            onClick={this.handleDownload}
          />

          <Divider style={{ margin: "20px 0" }} />

          {/* ---- Upload section ---- */}
          <p className={Classes.RUNNING_TEXT}>
            After adding new dimension instance names to the template, upload
            it here. Only <strong>new</strong> entries will be added — existing
            ones are never overwritten or deleted.
          </p>

          {/* Hidden native file input */}
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={this.handleFileChange}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
            }}
          >
            <Button
              icon="folder-open"
              text="Choose File (.xlsx)"
              onClick={this.openFilePicker}
            />
            {selectedFile ? (
              <Tag minimal intent={Intent.NONE}>
                {selectedFile.name}
              </Tag>
            ) : (
              <span style={{ color: "#aaa", fontSize: 13 }}>
                No file selected
              </span>
            )}
          </div>

          {uploadError && (
            <Callout
              intent={Intent.DANGER}
              icon="error"
              style={{ marginBottom: 10 }}
            >
              {uploadError}
            </Callout>
          )}

          <Button
            intent={Intent.SUCCESS}
            icon="upload"
            text="Upload"
            loading={isUploading}
            disabled={!selectedFile || isUploading}
            onClick={this.handleUpload}
          />
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              text="Close"
              onClick={this.handleClose}
              disabled={isUploading}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  private openFilePicker = () => {
    this.fileInputRef.current?.click();
  };

  private handleClose = () => {
    if (!this.state.isUploading) {
      this.setState({
        downloadError: "",
        selectedFile: null,
        uploadError: "",
      });
      this.props.onClose();
    }
  };

  private handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Reset file input so the same file can be re-selected after an error.
    e.target.value = "";
    this.setState({ selectedFile: file, uploadError: "" });
  };

  private handleDownload = async () => {
    this.setState({ isDownloading: true, downloadError: "" });
    try {
      const { url } = await api.getDimensionInstancesBulkTemplate(
        this.props.modelInstanceId
      );
      // The SAS URL has Content-Disposition: attachment set server-side,
      // so the browser downloads the file automatically without extra config.
      const anchor = document.createElement("a");
      anchor.href = url;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err) {
      const msg =
        err instanceof HTTPError
          ? err.body?.message || err.message
          : (err as Error).message || "Failed to download template.";
      this.setState({ downloadError: msg });
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  private handleUpload = async () => {
    const { selectedFile } = this.state;
    if (!selectedFile) {
      return;
    }
    this.setState({ isUploading: true, uploadError: "" });
    try {
      const result = await api.uploadDimensionInstancesBulk(
        this.props.modelInstanceId,
        selectedFile
      );
      this.props.onUploadSuccess(result);
    } catch (err) {
      const msg =
        err instanceof HTTPError
          ? err.body?.message || err.message
          : (err as Error).message ||
            "Upload failed. Please check the file and try again.";
      this.setState({ uploadError: msg });
      if (
        err instanceof HTTPError &&
        Array.isArray(err.body?.errors) &&
        err.body.errors.length > 0
      ) {
        downloadErrorLog(
          msg,
          err.body.errors as string[],
          this.props.modelInstanceId,
          this.state.selectedFile?.name ?? "upload"
        );
      }
    } finally {
      this.setState({ isUploading: false });
    }
  };
}

export default DimensionInstanceBulkDialog;
